import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import z from "zod";

import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { createClient } from "./supabase/admin";
import { Status, TableTypes } from "./supabase/enums";

/**
 * QR ordering: the guest is already sitting at a table, so there is no order
 * type to pick, no address, no timeslot and no customer to collect. Everything
 * this app needs to run that flow is resolved here — the merchant's
 * configuration, and which table a scanned code (or typed table number) points
 * at.
 *
 * The heavy lifting of turning a basket into POS order lines lives in
 * ./qr-orders.ts.
 */

export type QrPaymentMode = "at_table" | "online";

export interface QrConfiguration {
  enabled: boolean;
  paymentMode: QrPaymentMode;
  welcomeMessage?: string;
  showProductImages: boolean;
  allowComment: boolean;
  allowMultipleRounds: boolean;
}

export const qrConfigurationQueryOptions = () =>
  queryOptions({
    queryKey: ["qr-configuration"],
    queryFn: () => fetchQrConfiguration(),
  });

export const fetchQrConfiguration = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId } }): Promise<QrConfiguration> => {
    const supabase = createClient();

    const { data, error } = await supabase
      .from("qr_ordering_configuration")
      .select(
        "enabled, payment_mode, welcome_message, show_product_images, allow_comment, allow_multiple_rounds"
      )
      .eq("user_id", userId)
      .eq("status", Status.active)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Could not fetch QR ordering configuration", error);
      throw new Error("Could not fetch QR ordering configuration.");
    }

    // A merchant who never opened the settings screen has no row yet. That is
    // not an error — it just means QR ordering is off, which is also the
    // default for a row that exists.
    if (!data) {
      return {
        enabled: false,
        paymentMode: "at_table",
        showProductImages: true,
        allowComment: true,
        allowMultipleRounds: true,
      };
    }

    return {
      enabled: data.enabled,
      paymentMode: data.payment_mode === "online" ? "online" : "at_table",
      welcomeMessage: data.welcome_message ?? undefined,
      showProductImages: data.show_product_images,
      allowComment: data.allow_comment,
      allowMultipleRounds: data.allow_multiple_rounds,
    };
  });

/**
 * The payment mode that actually applies to one table.
 *
 * A guest on the pavement can be asked to pay before the drinks are poured
 * while the tables inside settle with a waiter, so the mode is a property of
 * the section, not of the venue. A section that set nothing — and a table that
 * was never put in a section at all — follows the merchant-wide setting.
 */
export function resolveQrPaymentMode({
  sectionPaymentMode,
  fallback,
}: {
  sectionPaymentMode: string | null | undefined;
  fallback: QrPaymentMode;
}): QrPaymentMode {
  if (sectionPaymentMode === "online") return "online";
  if (sectionPaymentMode === "at_table") return "at_table";
  return fallback;
}

export const qrTablePaymentModeQueryOptions = (tableId: string | undefined) =>
  queryOptions({
    queryKey: ["qr-table-payment-mode", tableId ?? null],
    // `enabled` already keeps this from running before the guest has a table;
    // the same guard is repeated here so the call types without an assertion.
    queryFn: () =>
      tableId === undefined
        ? Promise.resolve<QrPaymentMode>("at_table")
        : fetchQrTablePaymentMode({ data: { tableId } }),
    enabled: tableId !== undefined,
  });

/**
 * What the cart needs to label its own button: does this table pay now or
 * later. Nothing is trusted from it — createQrOrder resolves the same thing
 * again from the same two rows before it writes a line.
 */
export const fetchQrTablePaymentMode = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(z.object({ tableId: z.string().uuid() }))
  .handler(async ({ context: { userId }, data }): Promise<QrPaymentMode> => {
    const supabase = createClient();

    const [configuration, { data: table, error }] = await Promise.all([
      fetchQrConfiguration(),
      supabase
        .from("table")
        .select("id, location(qr_payment_mode)")
        .eq("id", data.tableId)
        .eq("user_id", userId)
        .neq("status", Status.trashed)
        .limit(1)
        .maybeSingle(),
    ]);

    if (error) {
      console.error("Could not resolve the QR payment mode", error);
      throw new Error("Could not resolve the QR payment mode.");
    }

    return resolveQrPaymentMode({
      sectionPaymentMode: table?.location?.qr_payment_mode,
      fallback: configuration.paymentMode,
    });
  });

export interface QrTable {
  id: string;
  reference: string;
  sectionName?: string;
}

export type ResolveQrTableResult =
  | { success: true; table: QrTable }
  | { success: false; code: "disabled" | "unknown_table" | "unassigned" };

/**
 * Turns a scanned token into a table. The token is a per-code secret, not the
 * table id: table ids are handed to the POS UI, and a guessable value would let
 * anyone put an order on any table in any venue.
 *
 * A code is its own record (public.qr_table_code) so stickers can be printed before
 * they are put on a table and moved between tables later. One that is not on a
 * table — yet, or any more — answers "unassigned", and the guest types their
 * table number instead.
 */
export const resolveQrTableByToken = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(z.object({ token: z.string().min(1).max(64) }))
  .handler(
    async ({ context: { userId }, data }): Promise<ResolveQrTableResult> => {
      const supabase = createClient();

      const configuration = await fetchQrConfiguration();
      if (!configuration.enabled) return { success: false, code: "disabled" };

      const { data: code, error } = await supabase
        .from("qr_table_code")
        .select("id, table(id, reference, type, status, location(name))")
        .eq("token", data.token)
        // The token is globally unique, but scoping to the merchant resolved
        // from the domain keeps one venue's code from working on another's
        // site if a token is ever reused after a restore.
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Could not resolve QR table by token", error);
        throw new Error("Could not resolve QR table.");
      }

      if (!code) return { success: false, code: "unknown_table" };

      const table = code.table;
      if (
        !table ||
        table.status === Status.trashed ||
        table.type !== TableTypes.location
      ) {
        return { success: false, code: "unassigned" };
      }

      return {
        success: true,
        table: {
          id: table.id,
          reference: table.reference,
          sectionName: table.location?.name ?? undefined,
        },
      };
    }
  );

/**
 * Fallback for a code that is not linked to a table (one generic sticker per
 * venue, or a code whose table was rotated): the guest types the number printed
 * on the table itself.
 *
 * Only dine-in tables can be typed in. Take-away, staff and representation
 * tables are internal bookkeeping destinations — a guest guessing a number
 * should not be able to drop food onto the staff tab.
 */
export const resolveQrTableByReference = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .inputValidator(z.object({ reference: z.string().trim().min(1).max(32) }))
  .handler(
    async ({ context: { userId }, data }): Promise<ResolveQrTableResult> => {
      const supabase = createClient();

      const configuration = await fetchQrConfiguration();
      if (!configuration.enabled) return { success: false, code: "disabled" };

      const { data: table, error } = await supabase
        .from("table")
        .select("id, reference, location(name)")
        .eq("user_id", userId)
        .eq("reference", data.reference)
        .eq("type", TableTypes.location)
        .neq("status", Status.trashed)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Could not resolve QR table by reference", error);
        throw new Error("Could not resolve QR table.");
      }

      if (!table) return { success: false, code: "unknown_table" };

      return {
        success: true,
        table: {
          id: table.id,
          reference: table.reference,
          sectionName: table.location?.name ?? undefined,
        },
      };
    }
  );
